"use client"

import { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { httpJson } from "@/lib/http"
import { CRM_API_BASE } from "@/lib/config"
import { fetchClientDetail } from "@/lib/client-detail-cache"

interface Business {
  id: number
  name?: string
  logo?: string | null
}

interface BusinessFilterDropdownProps {
  value: string
  onChange: (businessId: string, name: string | null, logo: string | null) => void
  disabled?: boolean
  variant?: "default" | "compact"
  widthClass?: string
  /** If provided, limit results to businesses belonging to this client. Empty/null disables the dropdown. */
  clientId?: string | null
}

const getBusinessLogoUrl = (b: Business): string | null => {
  if (!b.logo) return null
  return typeof b.logo === "string" ? b.logo : null
}

export function BusinessFilterDropdown({
  value,
  onChange,
  disabled = false,
  variant = "default",
  widthClass,
  clientId,
}: BusinessFilterDropdownProps) {
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [allBusinesses, setAllBusinesses] = useState<Business[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resolvedSelectedName, setResolvedSelectedName] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const isCompact = variant === "compact"
  const selectedId = value === "all" ? "" : value
  const isClientScoped = clientId != null && clientId !== "" && clientId !== "all"
  const hasNoBusinessesForClient = isClientScoped && !loading && allBusinesses.length === 0
  const isEffectivelyDisabled = disabled || (clientId !== undefined && !isClientScoped) || hasNoBusinessesForClient

  const inputClass = isCompact
    ? "h-7 text-xs border-gray-300 bg-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary pr-8"
    : "h-10 border border-neutral-300 bg-background focus:border-primary pr-8"

  const containerWidth = widthClass ?? (isCompact ? "w-52" : "w-56")

  const fetchClientBusinesses = async (cid: string, controller?: AbortController) => {
    try {
      setLoading(true)
      const response = await fetchClientDetail(cid)
      const list = Array.isArray(response.businesses) ? (response.businesses as Business[]) : []
      setAllBusinesses(list)
      setBusinesses(list)
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) return
      console.error("Error fetching client businesses:", error)
      setAllBusinesses([])
      setBusinesses([])
    } finally {
      setLoading(false)
    }
  }

  const fetchAllBusinesses = async (term = "", controller?: AbortController) => {
    try {
      setLoading(true)
      const url = term
        ? `${CRM_API_BASE}/business/?q=${encodeURIComponent(term)}&limit=20`
        : `${CRM_API_BASE}/business/?limit=20`
      const response = await httpJson<{ results?: Business[] }>(url, { signal: controller?.signal })
      const list = response.results ?? []
      if (term) {
        setBusinesses(list)
      } else {
        setAllBusinesses(list)
        setBusinesses(list)
      }
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) return
      console.error("Error fetching businesses:", error)
      setBusinesses([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    if (isClientScoped) {
      fetchClientBusinesses(clientId as string, controller)
    } else if (clientId === undefined) {
      fetchAllBusinesses("", controller)
    } else {
      setAllBusinesses([])
      setBusinesses([])
    }

    return () => controller.abort()
  }, [clientId])

  useEffect(() => {
    if (isClientScoped) {
      const term = searchTerm.trim().toLowerCase()
      if (!term) {
        setBusinesses(allBusinesses)
      } else {
        setBusinesses(allBusinesses.filter((b) => (b.name ?? "").toLowerCase().includes(term)))
      }
      return
    }
    if (clientId !== undefined) return

    if (abortRef.current) abortRef.current.abort()
    if (searchTerm.trim().length < 2) {
      setBusinesses([...allBusinesses])
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    const t = setTimeout(() => {
      fetchAllBusinesses(searchTerm, controller)
    }, 300)
    return () => {
      clearTimeout(t)
      controller.abort()
    }
  }, [searchTerm, allBusinesses, isClientScoped, clientId])

  useEffect(() => {
    if (!selectedId) {
      setResolvedSelectedName(null)
      return
    }
    const found = businesses.find((b) => b.id.toString() === selectedId) || allBusinesses.find((b) => b.id.toString() === selectedId)
    if (found?.name) {
      setResolvedSelectedName(found.name)
      return
    }
    let cancelled = false
    httpJson<Business>(`${CRM_API_BASE}/business/${selectedId}/`)
      .then((b) => {
        if (!cancelled) setResolvedSelectedName(b.name ?? null)
      })
      .catch(() => {
        if (!cancelled) setResolvedSelectedName(null)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, businesses, allBusinesses])

  const handleSelect = (b: Business | null) => {
    setSearchTerm("")
    setShowDropdown(false)
    if (!b) {
      onChange("all", null, null)
      return
    }
    onChange(b.id.toString(), b.name ?? null, getBusinessLogoUrl(b))
  }

  const clearFilter = () => {
    setSearchTerm("")
    setShowDropdown(false)
    onChange("all", null, null)
  }

  const placeholderText = clientId !== undefined && !isClientScoped
    ? "Select a client first"
    : hasNoBusinessesForClient
      ? "No businesses for client"
      : loading
        ? "Loading businesses..."
        : "All Businesses"

  const inputDisplayValue = searchTerm
    || (selectedId && !showDropdown ? (resolvedSelectedName ?? "") : "")
    || (!searchTerm && !selectedId && !showDropdown ? placeholderText : "")

  return (
    <div className={`relative ${containerWidth}`}>
      <Input
        placeholder={placeholderText}
        value={inputDisplayValue}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          setShowDropdown(true)
          if (selectedId) onChange("all", null, null)
        }}
        onFocus={() => {
          if (isEffectivelyDisabled) return
          setShowDropdown(true)
        }}
        onBlur={() => {
          setTimeout(() => setShowDropdown(false), 200)
        }}
        className={`${inputClass} ${isEffectivelyDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        disabled={loading || isEffectivelyDisabled}
      />
      <button
        type="button"
        onClick={() => {
          if (isEffectivelyDisabled) return
          if (!showDropdown) {
            setShowDropdown(true)
            setSearchTerm("")
          } else {
            setShowDropdown(false)
          }
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-primary-foreground p-1 rounded hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed z-10`}
        disabled={loading || isEffectivelyDisabled}
      >
        <svg
          className={`${isCompact ? "w-3 h-3" : "w-4 h-4"} transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {(searchTerm || selectedId) && !isEffectivelyDisabled && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            clearFilter()
          }}
          className={`absolute ${isCompact ? "right-7" : "right-9"} top-1/2 -translate-y-1/2 text-gray-600 hover:text-primary-foreground hover:bg-primary rounded p-1 z-20`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {showDropdown && !isEffectivelyDisabled && (
        <div className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal" style={{ left: "0", width: "100%" }}>
          {!isClientScoped && searchTerm.trim().length > 0 && searchTerm.trim().length < 2 ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>Type at least 2 characters to search...</div>
          ) : loading ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>Searching businesses...</div>
          ) : businesses.length === 0 ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>
              {searchTerm.trim() ? `No businesses found for "${searchTerm}"` : "No businesses available"}
            </div>
          ) : (
            <div>
              <div
                className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(null)}
              >
                All Businesses
              </div>
              {businesses.map((b) => (
                <div
                  key={b.id}
                  className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100 last:border-b-0`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(b)}
                >
                  {b.name ?? `Business #${b.id}`}
                </div>
              ))}
              {!isClientScoped && (searchTerm.trim() === "" ? allBusinesses.length === 20 : businesses.length === 20) && (
                <div className="p-2 text-[11px] text-muted-foreground border-t bg-gray-50">
                  💡 Type a business name to search for more results
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
